import { Icon } from '@drdropin-tech/theseus';
import { useLocale } from '@hooks/useLocale';
import { useNextAvailablePerClinic } from '@hooks/useNextAvailablePerClinic';
import { LegacyClinic } from '@packages/cortex-sdk';
import { localesNextAvailableWithTime } from '@packages/shared';
import * as Select from '@radix-ui/react-select';
import { formatRelative } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef } from 'react';

import { LoadingNextAvailable, NextAvailablePill } from './NextAvailable';

type DropDownProps = {
  clinics: LegacyClinic[];
  serviceId: string;
  practitionerId: string;
  handleSelectClinic: (clinic: LegacyClinic) => void;
  selectedClinic: LegacyClinic | null;
};
export default function ClinicDropdown({
  clinics,
  serviceId,
  practitionerId,
  selectedClinic,
  handleSelectClinic,
}: DropDownProps) {
  const { t } = useTranslation(['booking', 'common']);
  const hasAutoSelected = useRef(false);
  const locale = useLocale();

  const {
    clinicLoadingStates,
    completedLoadingNextAvailableForAllClinics,
    availabilityMap,
  } = useNextAvailablePerClinic(clinics, serviceId, practitionerId);

  useEffect(() => {
    if (
      !completedLoadingNextAvailableForAllClinics ||
      hasAutoSelected.current ||
      availabilityMap.size === 0
    ) {
      return;
    }

    let nearestId: string | null = null;
    let nearestTime = Infinity;

    availabilityMap.forEach((time, clinicId) => {
      if (time < nearestTime) {
        nearestTime = time;
        nearestId = clinicId;
      }
    });
    if (!nearestId) return;
    const clinic = clinics.find((c) => c.id === nearestId);
    if (clinic) {
      hasAutoSelected.current = true;
      handleSelectClinic(clinic);
    }
  }, [
    completedLoadingNextAvailableForAllClinics,
    availabilityMap,
    clinics,
    handleSelectClinic,
  ]);

  useEffect(() => {
    hasAutoSelected.current = false;
  }, [serviceId, practitionerId]);

  if (!practitionerId || clinics.length === 0) {
    return null;
  }

  const cityMap = new Map<string, LegacyClinic[]>();
  clinics.forEach((clinic) => {
    const city = clinic.address.city;
    if (!cityMap.has(city)) {
      cityMap.set(city, []);
    }
    cityMap.get(city)!.push(clinic);
  });

  const sortedCities = [...cityMap.entries()]
    .sort(([a], [b]) => {
      if (a.toLowerCase() === 'oslo') return -1;
      if (b.toLowerCase() === 'oslo') return 1;
      return a.localeCompare(b);
    })
    .map(
      ([city, cityClinic]) =>
        [
          city,
          cityClinic.sort((a, b) => {
            const timeA = availabilityMap.get(a.id) ?? Infinity;
            const timeB = availabilityMap.get(b.id) ?? Infinity;
            if (timeA !== timeB) return timeA - timeB;
            return a.name.localeCompare(b.name);
          }),
        ] as const,
    );

  return (
    <Select.Root
      value={selectedClinic?.id}
      onValueChange={(value) => {
        const clinic = clinics.find((c) => c.id === value);
        if (clinic) handleSelectClinic(clinic);
      }}
    >
      <Select.Trigger className="flex items-center justify-between rounded-lg bg-white p-3 text-black">
        <div className="flex items-center gap-4 text-left text-sm">
          <Icon name="MapPin" iconSize="sm" className="shrink-0 text-black" />
          <Select.Value
            placeholder={
              selectedClinic?.name ??
              (!completedLoadingNextAvailableForAllClinics
                ? t('common:loading') + '...'
                : t('steps.select_datetime.select_clinic'))
            }
          />
        </div>

        <Icon
          name="ChevronDown"
          iconSize="md"
          className="shrink-0 text-black"
        />
      </Select.Trigger>

      <Select.Portal>
        <Select.Content
          style={{
            width: 'var(--radix-select-trigger-width)',
            maxHeight: 'var(--radix-select-content-available-height)',
          }}
          position="popper"
          sideOffset={5}
          className="rounded-lg bg-white py-4"
        >
          <Select.ScrollUpButton className="flex h-[25px] cursor-default items-center justify-center ">
            <Icon
              name="ChevronUp"
              iconSize="md"
              className="shrink-0 text-black"
            />
          </Select.ScrollUpButton>
          <Select.Viewport
            style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
          >
            {sortedCities.map(([city, cityClinic]) => (
              <Select.Group key={city}>
                <Select.Label className="px-6 text-[16px] font-medium">
                  {city}
                </Select.Label>
                {cityClinic.map((clinic, index) => {
                  const isClinicAvailabilityLoading =
                    clinicLoadingStates.get(clinic.id) ?? true;
                  const hasAvailabilityAtClinic = availabilityMap.has(
                    clinic.id,
                  );

                  return (
                    <Select.Item
                      key={clinic.id}
                      value={clinic.id}
                      disabled={
                        completedLoadingNextAvailableForAllClinics &&
                        !hasAvailabilityAtClinic
                      }
                      className="[&[data-disabled]]:text-gray-dark flex cursor-pointer items-start justify-between gap-1 px-6 py-1 text-[16px] outline-none [&[data-disabled]]:cursor-not-allowed [&[data-highlighted]]:bg-[#e5e5e5]"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-2 shrink-0">
                          <Select.ItemIndicator>
                            <Icon
                              name="Check"
                              iconSize="xs"
                              className=" text-black"
                            />
                          </Select.ItemIndicator>
                        </div>
                        <Select.ItemText asChild>
                          <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">
                            {clinic.name}
                          </span>
                        </Select.ItemText>
                      </div>
                      <div className="shrink-0">
                        {isClinicAvailabilityLoading ? (
                          <LoadingNextAvailable index={index} minimize />
                        ) : hasAvailabilityAtClinic ? (
                          <NextAvailablePill
                            label={formatRelative(
                              availabilityMap.get(clinic.id)!,
                              new Date(),
                              {
                                locale: localesNextAvailableWithTime[locale],
                              },
                            )}
                            minimize
                          />
                        ) : null}
                      </div>
                    </Select.Item>
                  );
                })}
              </Select.Group>
            ))}
          </Select.Viewport>
          <Select.ScrollDownButton className="flex h-[25px] cursor-default items-center justify-center">
            <Icon
              name="ChevronDown"
              iconSize="md"
              className="shrink-0 text-black"
            />
          </Select.ScrollDownButton>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
